// ─────────────────────────────────────────────────────────────────────────────
// ONE INVITE FORM, TWO PAGES.
//
// 🚨 THERE WERE TWO OF THESE. The Partners page carried the full form — single
// and batch, the partner/account toggle, the 409 that names an existing
// partner's rep — and the Accounts page carried AccountInviteModal, a thinner
// copy with no batch, no toggle, and its own required-field list. Both posted to
// the same endpoint. Two forms in front of one route is the mirror-drift shape
// this codebase has paid for in program terms, partner statuses, the FICO ladder
// and the showing-time grid; the only reason it had not bitten yet is that the
// thin copy was new.
//
// 🔑 THE PAGES DIFFER BY ONE PROP — which card the toggle starts on. Everything
// that could actually drift — which endpoint, which required fields, what the
// error says, what the button sends — is here once.
//
// 🚨 AND THE TYPE FIELD IS GONE ENTIRELY, which settles a question the previous
// version only patched. It was labelled "Partner Type" or "Account Type"
// depending on the toggle, with the same three values under two vocabularies —
// and the label was the only honest part of that, because the value was never
// the invite's to decide. /doorlypro/start asks the invitee, and their answer is
// what registration stores.
import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ⚠️ partner_type STAYS IN THE PAYLOAD SHAPE, empty. The server still accepts it
// — the batch format has an optional Type column — and sending '' rather than
// omitting the key keeps one request shape for both paths.
const BLANK = { first_name: '', last_name: '', phone: '', email: '', partner_type: '', company: '', kind: 'partner' };

// 🚨 partner_type IS NOT ASKED HERE ANY MORE, AND IT NEVER DECIDED ANYTHING.
// /doorlypro/start asks the invitee what they are; their answer is what
// registration stores. The invite's copy of it only ever PRE-FILLED that
// question so it could be skipped — a rep guessing wrong was silently corrected,
// and a rep guessing right saved one tap. Making somebody answer a question that
// belongs to the recipient, in order to send them an invitation, is friction
// that bought a tap.
//
// ⚠️ THE FIELD STILL EXISTS ON partner_invites and the batch format keeps its
// optional Type column, so a CSV that has the answer can still supply it. What
// changed is that nothing REQUIRES a guess.
const REQUIRED = [
  ['first_name', 'First name'], ['last_name', 'Last name'], ['email', 'Email'],
  ['phone', 'Phone'], ['company', 'Company'],
];

const label = { fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '5px' };
const input = { width: '100%', padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' };
const req = <span style={{ color: '#ff082f' }}>*</span>;

// ⚠️ PARSED CLIENT-SIDE, VALIDATED SERVER-SIDE. This splits a paste into rows; it
// deliberately does NOT decide which are sendable. That answer comes from the
// same checks the single invite runs, so a CSV row and a typed form cannot
// disagree — see backend/utils/partnerInviteChecks.js.
const parseBatch = (text) => {
  const lines = String(text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const out = [];
  for (const line of lines) {
    const c = line.split(/\t|,/).map((x) => x.trim());
    // A header row is common in a spreadsheet paste; skip one that looks like
    // labels rather than trying to map columns by name.
    if (/^first[\s_]?name$/i.test(c[0] || '')) continue;
    out.push({ first_name: c[0] || '', last_name: c[1] || '', email: c[2] || '',
      phone: c[3] || '', partner_type: c[4] || '', company: c[5] || '' });
  }
  return out;
};

// 🔑 defaultKind, NOT lockKind. The Accounts page used to HIDE the toggle, so
// the two entry points were different forms wearing the same component — and the
// card pair is the clearest part of this form: it says in two lines what the two
// invitations are for. Both pages show it; the Accounts button just starts on
// the account card.
export default function PartnerInviteModal({ token, open, onClose, onSent, defaultKind = 'partner' }) {
  const [form, setForm] = useState({ ...BLANK, kind: defaultKind });
  const [mode, setMode] = useState('single');
  const [batchText, setBatchText] = useState('');
  const [batchPreview, setBatchPreview] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const kind = form.kind;
  const isAccount = kind === 'account';
  const reset = () => { setForm({ ...BLANK, kind: defaultKind }); setBatchText(''); setBatchPreview(null); };
  const close = () => { setError(null); setBatchPreview(null); onClose(); };

  const submit = async () => {
    const missing = REQUIRED.filter(([k]) => !String(form[k] || '').trim());
    if (missing.length) { setError(`${missing[0][1]} is required.`); return; }
    setBusy(true); setError(null);
    try {
      const res = await axios.post(`${API}/api/admin/partners/invite`, { ...form, kind },
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        setSuccess(true);
        reset();
        onSent?.();
        setTimeout(() => { onClose(); setSuccess(false); }, 2500);
      }
    } catch (e) {
      // ⚠️ The 409 body NAMES the existing partner and their rep. Surfacing it
      // verbatim is the point of the trade recorded in
      // backend/utils/partnerInviteChecks.js — a generic "already exists" sends
      // the rep to interrupt the wrong person to find out whose account it is.
      setError(e.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  const runBatch = async (confirm) => {
    const rows = parseBatch(batchText);
    if (!rows.length) { setError('Nothing to preview — paste some rows first.'); return; }
    setBatchBusy(true); setError(null);
    try {
      // 🚨 THE KIND GOES WITH THE BATCH. It was hardcoded 'partner' server-side,
      // so a batch pasted on the Accounts page would have sent partner-worded
      // email to people invited as firms.
      const r = await axios.post(`${API}/api/admin/partners/invite/batch`, { rows, confirm, kind },
        { headers: { Authorization: `Bearer ${token}` } });
      setBatchPreview(r.data);
      if (confirm) onSent?.();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setBatchBusy(false); }
  };

  const noun = isAccount ? 'Account' : 'Partner';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '440px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        {success ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: '20px', fontWeight: '800', color: '#111827' }}>Invitation sent</h3>
            <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
              {isAccount
                ? 'They’ll receive an email about setting their firm up on DoorlyPro.'
                : 'They’ll receive an email with a link to register for DoorlyPro.'}
            </p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
              <div>
                <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: '800', color: '#111827' }}>
                  {isAccount ? 'Invite an Account' : 'Invite a Partner'}
                </h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                  {isAccount ? 'They’ll set their firm up and can invite their team' : 'They’ll receive an email to join DoorlyPro'}
                </p>
              </div>
              <button onClick={close} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#9ca3af' }}>✕</button>
            </div>

            <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e5e7eb', marginBottom: 18 }}>
              {[['single', `One ${noun.toLowerCase()}`], ['batch', 'Several at once']].map(([k, text]) => (
                <button key={k} type="button" onClick={() => { setMode(k); setError(null); setBatchPreview(null); }}
                  style={{ padding: '8px 14px', fontSize: 13, fontWeight: 700, border: 'none', background: 'transparent',
                    borderBottom: mode === k ? '2px solid #3a6ad9' : '2px solid transparent',
                    color: mode === k ? '#3a6ad9' : '#6b7280', cursor: 'pointer' }}>
                  {text}
                </button>
              ))}
            </div>

            {mode === 'batch' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>
                  Paste rows from a spreadsheet — one per line, tab or comma separated:
                  <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#374151', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', marginTop: 6 }}>
                    First name, Last name, Email, Phone, Type, Company
                  </div>
                </div>
                <textarea value={batchText} onChange={(e) => { setBatchText(e.target.value); setBatchPreview(null); }}
                  rows={7} placeholder={'Ada\tLovelace\tada@northshore.com\t5125550142\trealtor\tNorthshore Realty'}
                  style={{ ...input, fontSize: 12, fontFamily: 'monospace' }} />

                {/* ⚠️ NOTHING IS SENT UNTIL THE PREVIEW HAS BEEN SEEN AND
                    CONFIRMED. The server defaults to preview and requires an
                    explicit confirm, so a mis-click here cannot mail anyone. */}
                {batchPreview && (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, maxHeight: 240, overflow: 'auto' }}>
                    <div style={{ padding: '8px 12px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 12, fontWeight: 700, color: '#374151' }}>
                      {batchPreview.preview
                        ? `${batchPreview.sendable} of ${batchPreview.total} ready to send`
                        : `${batchPreview.sent} of ${batchPreview.total} sent`}
                    </div>
                    {batchPreview.results.map((r) => (
                      <div key={r.index} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '7px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                        <span style={{ color: '#111827', flexShrink: 0 }}>{`${r.first_name} ${r.last_name}`.trim() || r.email || '(blank row)'}</span>
                        <span style={{ color: r.ok ? '#166534' : '#b91c1c', textAlign: 'right' }}>
                          {r.ok ? (batchPreview.preview ? 'Ready' : 'Sent') : r.error}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {error && <div style={{ background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#ff082f' }}>{error}</div>}

                <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                  <button onClick={close}
                    style={{ flex: 1, padding: '11px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Close</button>
                  {batchPreview && batchPreview.preview && batchPreview.sendable > 0 ? (
                    <button onClick={() => runBatch(true)} disabled={batchBusy}
                      style={{ flex: 2, padding: '11px', background: batchBusy ? '#e5e7eb' : '#3a6ad9', color: batchBusy ? '#9ca3af' : '#fff', border: 'none', borderRadius: '8px', cursor: batchBusy ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px' }}>
                      {batchBusy ? 'Sending…' : `Send ${batchPreview.sendable} ${noun.toLowerCase()} invitation${batchPreview.sendable === 1 ? '' : 's'} →`}
                    </button>
                  ) : (
                    <button onClick={() => runBatch(false)} disabled={batchBusy || !batchText.trim()}
                      style={{ flex: 2, padding: '11px', background: (batchBusy || !batchText.trim()) ? '#e5e7eb' : '#3a6ad9', color: (batchBusy || !batchText.trim()) ? '#9ca3af' : '#fff', border: 'none', borderRadius: '8px', cursor: (batchBusy || !batchText.trim()) ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px' }}>
                      {batchBusy ? 'Checking…' : 'Preview'}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* ⚠️ THE TOGGLE IS HIDDEN, NOT DISABLED, WHEN THE PAGE FIXES THE
                    KIND. The Accounts page invites firms; offering a choice it
                    would then ignore is worse than offering none.

                    ⚠️ IT DOES NOT PRE-ANSWER "agent or broker". That question
                    selects the enterprise registration door, and whoever saves
                    banking on a new account becomes its payee contact — not
                    something a rep's choice of template should confer. */}
                {(
                  <div>
                    <label style={label}>Invitation type</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[['partner', 'Invite Partner', 'One person joining'],
                        ['account', 'Invite Account', 'Someone bringing a team']].map(([v, text, hint]) => (
                        <button key={v} type="button" onClick={() => setForm((p) => ({ ...p, kind: v }))}
                          style={{ flex: 1, textAlign: 'left', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                            border: `2px solid ${form.kind === v ? '#3a6ad9' : '#e5e7eb'}`,
                            background: form.kind === v ? '#f5f8ff' : '#fff' }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>{text}</div>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: 2 }}>{hint}</div>
                        </button>
                      ))}
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: 4 }}>
                      Changes the wording of the email. Both ask the same questions when they register.
                    </div>
                  </div>
                )}

                <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={label}>First Name {req}</label>
                    <input style={input} value={form.first_name} placeholder="Jane"
                      onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))} />
                  </div>
                  <div>
                    <label style={label}>Last Name {req}</label>
                    <input style={input} value={form.last_name} placeholder="Smith"
                      onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label style={label}>Email {req}</label>
                  <input style={input} type="email" value={form.email} placeholder="jane@brokerage.com"
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Phone {req}</label>
                  <input style={input} type="tel" value={form.phone} placeholder="(512) 000-0000"
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Company {req}</label>
                  <input style={input} value={form.company} placeholder="Northshore Realty"
                    onChange={(e) => setForm((p) => ({ ...p, company: e.target.value }))} />
                  {/* Matched to an existing account by normalised name, so
                      "Northshore Realty LLC" and "Northshore Realty" land on one. */}
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: 4 }}>Matched to an existing account where one exists.</div>
                </div>

                {error && <div style={{ background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#ff082f' }}>{error}</div>}

                <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                  <button onClick={close}
                    style={{ flex: 1, padding: '11px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Cancel</button>
                  {/* 🔑 THE BUTTON NAMES WHAT IT SENDS. It read "Send Invitation →"
                      for both, so the toggle changed the email and nothing on the
                      button confirmed which one was about to go out. */}
                  <button onClick={submit} disabled={busy}
                    style={{ flex: 2, padding: '11px', background: busy ? '#e5e7eb' : '#3a6ad9', color: busy ? '#9ca3af' : '#fff', border: 'none', borderRadius: '8px', cursor: busy ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px' }}>
                    {busy ? 'Sending…' : `Send ${noun} Invitation →`}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
